import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ADD_ONS,
  PLANS,
  TIER_ORDER,
  bestDiscountPct,
  discountPct,
  formatCurrency,
} from '@dca/shared';

import { ItemIcon } from '../components/ItemIcon.tsx';
import { api } from '../lib/api.ts';
import { NEXT_THEME, useTheme } from '../lib/theme.ts';
import { number, unitMoney } from '../lib/format.ts';

type Showcase = Awaited<ReturnType<typeof api.market.showcase>>['items'][number];

/**
 * Public landing page.
 *
 * Leads with the thing that is genuinely unusual about this product: it works
 * before you connect anything, because the market data is public. Most
 * analytics tools ask for credentials first and show value second.
 *
 * Everything numeric here is live. There are no mocked screenshots, no invented
 * shop counts and no testimonials - putting made-up figures on the front of a
 * pricing product would be a strange way to ask for trust, and the real numbers
 * are better than anything worth inventing.
 */
export function LandingPage() {
  const stats = usePublic(() => api.market.stats());
  const showcase = usePublic(() => api.market.showcase());
  const items = showcase.data?.items ?? [];
  const hero = items[0] ?? null;
  const best = bestDiscountPct();
  const [theme, setTheme] = useTheme();

  return (
    <div className="landing">
      {/* Sign-in sits apart from the hero's two calls to action: those are for
          people arriving, this is for people coming back. */}
      <div className="landing-topbar">
        {/* The theme control lives here too, not only in the signed-in shell.
            The preference is per-origin localStorage, so somebody arriving from
            a different host - or for the first time - lands on whatever their
            OS prefers with no way to change it until they have an account. */}
        <button
          className="landing-topbar-link theme-toggle"
          onClick={() => setTheme(NEXT_THEME[theme])}
          title={`Theme: ${theme}. Click for ${NEXT_THEME[theme]}.`}
          aria-label={`Switch theme, currently ${theme}`}
        >
          {theme === 'dark' ? '◑' : theme === 'light' ? '◐' : '◓'}
          <span className="theme-word">{theme}</span>
        </button>
        <Link to="/login" className="landing-topbar-link">
          Log in
        </Link>
      </div>

      <header className="landing-hero">
        {/* Layered backdrop: drifting colour blooms, a plotting grid, and a
            ring of real item textures orbiting the headline. All decorative and
            all inert - the copy below is what carries the page. */}
        <div className="hero-bg" aria-hidden="true">
          <span className="hero-aurora a" />
          <span className="hero-aurora b" />
          <span className="hero-aurora c" />
          <span className="hero-grid" />
        </div>

        <div className="hero-orbit" aria-hidden="true">
          {ORBIT.map((entry) => (
            <span
              key={entry.material}
              className="hero-orbit-item"
              style={{
                left: `${entry.x}%`,
                top: `${entry.y}%`,
                animationDelay: `${entry.delay}s`,
                animationDuration: `${entry.duration}s`,
              }}
            >
              <ItemIcon material={entry.material} size={entry.size} />
            </span>
          ))}
        </div>

        <div className="hero-content">
          <div className="landing-brand">
            <span className="brand-mark">CA</span>
            ChestShop Analytics
          </div>

          <h1>
            Smarter shops.<span className="hero-accent"> Less legwork.</span>
          </h1>

          <p className="lede">
            Chestr shows you exactly where your prices stand, when buyers are shopping, and what to
            stock next — across every chest in Redmont. Stop guessing. Start selling smarter.
          </p>

          <div className="landing-cta">
            <Link to="/login?role=store_owner">
              <button className="primary lg">List Your Shop</button>
            </Link>
            <Link to="/login?role=buyer">
              <button className="lg secondary">Browse the Market</button>
            </Link>
          </div>
        </div>
      </header>

      <div className="landing-strip" aria-hidden="true">
        <div className="landing-strip-track">
          {[...STRIP, ...STRIP].map((material, index) => (
            <ItemIcon key={`${material}-${index}`} material={material} size={30} />
          ))}
        </div>
      </div>

      {/* Scale, below the fold rather than in the hero. These are real server
          totals and worth showing, but four big numbers were competing with the
          headline for the one thing a first-time reader should take in. */}
      <div className="landing-stats">
        <Stat
          value={stats.data?.stats ? number(stats.data.stats.activeShops) : null}
          label="chestshops tracked"
        />
        <Stat
          value={stats.data?.stats ? number(stats.data.stats.distinctItems) : null}
          label="distinct items"
        />
        <Stat
          value={stats.data?.stats ? compact(stats.data.stats.totalSales) : null}
          label="recorded sales"
        />
        <Stat
          value={stats.data?.stats ? `$${compact(Number(stats.data.stats.totalVolume))}` : null}
          label="traded volume"
        />
      </div>

      {hero && <WorkedExample item={hero} />}

      {/* The product's core output, on real items, with real numbers. */}
      <section className="landing-section">
        <div className="landing-section-head">
          <span className="landing-eyebrow">Every item you sell</span>
          <h2>Four numbers, not one</h2>
          <p className="lede-sm">
            Outliers trimmed before anything is calculated, so one troll listing at $1,000,000
            cannot define your market.
          </p>
        </div>

        <div className="showcase-grid">
          {items.map((item) => (
            <div className="showcase-card" key={item.itemKey}>
              <div className="showcase-head">
                <ItemIcon material={item.material} size={30} alt={item.itemKey} />
                <div>
                  <div className="showcase-name">{item.itemKey}</div>
                  <div className="small muted">
                    {number(item.shops)} shops in stock
                    {item.band.outliersRemoved > 0 &&
                      ` - ${item.band.outliersRemoved} outliers dropped`}
                  </div>
                </div>
              </div>
              <div className="showcase-band">
                <span className="head">Low</span>
                <span className="head">Average</span>
                <span className="head">High</span>
                <span className="head rec">Suggested</span>
                <span>{unitMoney(item.band.low)}</span>
                <span>{unitMoney(item.band.average)}</span>
                <span>{unitMoney(item.band.high)}</span>
                <span className="rec">{unitMoney(item.band.recommended)}</span>
              </div>
              <div className="showcase-why small muted">{item.band.recommendedReason}</div>
            </div>
          ))}
          {showcase.data == null &&
            [0, 1, 2, 3, 4, 5].map((index) => (
              <div className="showcase-card loading" key={index}>
                <span className="skeleton block" />
              </div>
            ))}
        </div>
      </section>

      {/* The pitch stated plainly: what running a shop is like either way. */}
      <section className="landing-section">
        <div className="landing-section-head">
          <h2>The difference it makes</h2>
        </div>
        <div className="compare">
          <div className="compare-col before">
            <h3>Without it</h3>
            <ul>
              {BEFORE.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
          <div className="compare-col after">
            <h3>With it</h3>
            <ul>
              {AFTER.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Three audiences, three blocks. Each one leads with what that reader
          gets, not with what the product does. */}
      <section className="landing-section">
        <div className="audience">
          {AUDIENCES.map((audience) => (
            <article className={`audience-card ${audience.key}`} key={audience.key}>
              <div className="audience-head">
                <span className="audience-emoji" aria-hidden="true">
                  {audience.emoji}
                </span>
                <div>
                  <h3>
                    {audience.title}
                    <span className="audience-tag">{audience.tag}</span>
                  </h3>
                </div>
              </div>
              <p className="audience-lede">{audience.lede}</p>
              <ul className="audience-list">
                {audience.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <Link to={audience.href} className="audience-cta">
                <button className={audience.key === 'owners' ? 'primary' : ''}>
                  {audience.cta}
                </button>
              </Link>
            </article>
          ))}
        </div>

        {/* The extras that did not fit a headline bullet but still sell. */}
        <div className="also">
          <span className="also-label">Also included</span>
          <ul>
            {ALSO.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-section-head">
          <h2>Up and running in three steps</h2>
        </div>
        <ol className="landing-steps">
          {STEPS.map((step, index) => (
            <li key={step.title}>
              <span className="landing-step-n">{index + 1}</span>
              <div>
                <h3>{step.title}</h3>
                <p className="small muted">{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Six tiers in one horizontal rail rather than wrapping to two rows:
          they are a single ordered scale, and breaking them across lines makes
          Gold look like the start of a second, cheaper range. */}
      <section className="landing-section">
        <div className="landing-section-head">
          {best != null && <span className="landing-eyebrow">Launch pricing - up to {best}% off</span>}
          <h2>Every plan is reduced right now</h2>
          <p className="lede-sm">
            Paid in in-game dollars, and sized by how big your shop is - every paid tier has the
            full analytics. These are launch prices, held for as long as you stay subscribed.
          </p>
        </div>

        <div className="tier-rail" role="group" aria-label="Plans">
          {TIER_ORDER.map((tier) => {
            const plan = PLANS[tier];
            const cut = discountPct(plan);
            return (
              <article
                className={`tier-card ${tier} ${tier === 'wood' ? 'featured' : ''} ${cut != null ? 'discounted' : ''}`}
                key={tier}
              >
                {tier === 'wood' && <span className="tier-flag">first month free</span>}
                <div className="tier-icon">
                  <ItemIcon material={TIER_MATERIAL[tier]} size={30} />
                </div>
                <h3>{plan.name}</h3>
                {/* The former price stays visible beside the new one: a cut
                    nobody can see is just a lower number. */}
                {cut != null && <span className="tier-save">save {cut}%</span>}
                <div className="tier-price">
                  {plan.price === 0 ? 'Free' : formatCurrency(plan.price)}
                  {plan.price > 0 && <span className="tier-per">/mo</span>}
                </div>
                {plan.listPrice != null && (
                  <div className="tier-was">
                    <s>{formatCurrency(plan.listPrice)}</s>
                    <span className="tier-off">
                      {formatCurrency(plan.listPrice - plan.price)} off every month
                    </span>
                  </div>
                )}
                <div className="tier-headline">
                  {plan.maxChestshops == null
                    ? 'Unlimited chestshops'
                    : `${number(plan.maxChestshops)} chestshops`}
                  <span className="muted">
                    {plan.maxLocations == null
                      ? ' for any number of locations'
                      : ` for up to ${plan.maxLocations} location${plan.maxLocations === 1 ? '' : 's'}`}
                  </span>
                </div>
                <ul className="tier-list">
                  {TIER_POINTS[tier].map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
                <Link to="/login" className="tier-cta">
                  <button className={tier === 'wood' ? 'primary' : ''}>
                    {plan.price === 0 ? 'Start free' : `Choose ${plan.name}`}
                  </button>
                </Link>
              </article>
            );
          })}
        </div>

        <p className="small muted landing-note">
          Between tiers? Add-ons cover the gap - {formatCurrency(ADD_ONS.extraLocation.price)} for
          an extra location, {formatCurrency(ADD_ONS.extraChestshops.price)} per another{' '}
          {ADD_ONS.extraChestshops.blockSize} chestshops - rather than forcing a jump to the tier
          above.
        </p>
      </section>

      <section className="landing-section">
        <div className="landing-section-head">
          <h2>Questions people ask</h2>
        </div>
        <div className="faq">
          {FAQ.map((entry) => (
            <details className="faq-item" key={entry.q}>
              <summary>{entry.q}</summary>
              <p className="small muted">{entry.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="landing-closer">
        <h2>One market. Every shop. Zero walking.</h2>
        <p className="lede-sm">
          Join free as a buyer — upgrade your shop when you’re ready to sell smarter.
        </p>
        <div className="landing-cta" style={{ marginTop: 22 }}>
          <Link to="/login?role=buyer">
            <button className="primary lg">Browse the Market</button>
          </Link>
          <Link to="/login?role=store_owner">
            <button className="lg secondary">List Your Shop</button>
          </Link>
        </div>
      </section>

      <footer className="landing-footer">
        <p className="small muted">
          Built on the official DemocracyCraft Economy API. Prices, stock and shops are read from
          the server itself.
        </p>
      </footer>

    </div>
  );
}

/**
 * One real item, priced out.
 *
 * The most persuasive thing available is a live number with the workings shown,
 * so this takes the market's best-stocked item and walks through the gap
 * between the bottom of its range and what demand actually supports. Nothing is
 * cherry-picked to flatter the product - it is simply the first showcase row.
 */
function WorkedExample({ item }: { item: Showcase }) {
  const gap = Math.round((item.band.recommended - item.band.low) * 100) / 100;

  return (
    <section className="worked">
      <div className="worked-copy">
        <span className="landing-eyebrow">A real item, right now</span>
        <h2>
          {item.itemKey} sits between {unitMoney(item.band.low)} and {unitMoney(item.band.high)}.
        </h2>
        <p className="lede-sm">
          {number(item.shops)} shops stock it. We trim the fat-fingered listings, read how fast it
          actually moves, and tell you where to sit in that range - and why.
        </p>
        <ul className="worked-points">
          <li>
            <strong>{unitMoney(item.band.average)}</strong> is the market average across{' '}
            {number(item.band.sampleSize)} stocked shops
            {item.band.outliersRemoved > 0 && (
              <>
                , after dropping {number(item.band.outliersRemoved)} outlier
                {item.band.outliersRemoved === 1 ? '' : 's'}
              </>
            )}
            .
          </li>
          <li>
            <strong>{unitMoney(item.band.recommended)}</strong> is what demand supports.{' '}
            {item.band.recommendedReason}
          </li>
          {gap > 0 && (
            <li>
              A shop sitting at the bottom of that range is charging{' '}
              <strong>{unitMoney(gap)} less per unit</strong> than the data supports.
            </li>
          )}
        </ul>
        <Link to="/login">
          <button className="primary">Check your own prices</button>
        </Link>
      </div>

      <div className="worked-visual">
        <div className="worked-item">
          <ItemIcon material={item.material} size={40} alt={item.itemKey} />
          <div>
            <div className="worked-item-name">{item.itemKey}</div>
            <div className="small muted">{number(item.shops)} shops in stock</div>
          </div>
        </div>
        <BandRuler band={item.band} />
      </div>
    </section>
  );
}

/** The cleaned range as a ruler, with average and recommendation marked. */
function BandRuler({ band }: { band: Showcase['band'] }) {
  const at = (value: number) => {
    if (!(band.high > band.low)) return 50;
    return Math.max(4, Math.min(96, ((value - band.low) / (band.high - band.low)) * 100));
  };

  return (
    <div className="ruler">
      <div className="ruler-track">
        <span className="ruler-fill" style={{ width: `${at(band.recommended)}%` }} />
        <span className="ruler-pin avg" style={{ left: `${at(band.average)}%` }} />
        <span className="ruler-pin rec" style={{ left: `${at(band.recommended)}%` }} />
      </div>
      <div className="ruler-ends">
        <span>low {unitMoney(band.low)}</span>
        <span>high {unitMoney(band.high)}</span>
      </div>
      <div className="ruler-key">
        <span>
          <i className="key-dot avg" /> average {unitMoney(band.average)}
        </span>
        <span>
          <i className="key-dot rec" /> suggested {unitMoney(band.recommended)}
        </span>
      </div>
    </div>
  );
}

/** Server totals abbreviated, so 2,775,788 reads as 2.8M on a marketing tile. */
function compact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(Math.round(value));
}

function Stat({ value, label }: { value: string | null; label: string }) {
  return (
    <div className="landing-stat">
      <div className="landing-stat-value">{value ?? <span className="skeleton" />}</div>
      <div className="landing-stat-label">{label}</div>
    </div>
  );
}

/**
 * Loads public data without the auth plumbing the dashboard uses.
 *
 * A failure here is silent on purpose: the page still reads correctly with the
 * live figures missing, and an error banner on the front door would cost more
 * trust than the numbers earn.
 */
function usePublic<T>(load: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data };
}

/**
 * Item textures scattered around the headline.
 *
 * Positions are hand-placed to sit clear of the centre column where the copy
 * lives, and the whole layer is hidden below the breakpoint where that column
 * grows to fill the width.
 */
const ORBIT = [
  { material: 'DIAMOND', x: 7, y: 16, size: 40, delay: 0, duration: 7.5 },
  { material: 'GOLD_INGOT', x: 15, y: 52, size: 32, delay: 1.1, duration: 8.4 },
  { material: 'IRON_INGOT', x: 4, y: 74, size: 28, delay: 2.3, duration: 9.1 },
  { material: 'EMERALD', x: 88, y: 13, size: 36, delay: 0.6, duration: 8.1 },
  { material: 'NETHERITE_INGOT', x: 92, y: 48, size: 30, delay: 1.8, duration: 7.9 },
  { material: 'AMETHYST_SHARD', x: 82, y: 76, size: 26, delay: 2.9, duration: 8.8 },
  { material: 'REDSTONE', x: 24, y: 84, size: 24, delay: 3.4, duration: 9.4 },
  { material: 'LAPIS_LAZULI', x: 74, y: 90, size: 22, delay: 1.4, duration: 8.6 },
];

const STRIP = [
  'DIAMOND', 'IRON_INGOT', 'GOLD_INGOT', 'EMERALD', 'REDSTONE', 'LAPIS_LAZULI',
  'NETHERITE_INGOT', 'COAL', 'COPPER_INGOT', 'AMETHYST_SHARD', 'QUARTZ', 'ENDER_PEARL',
  'BLAZE_ROD', 'GUNPOWDER', 'OAK_LOG', 'STONE', 'GLASS', 'FIREWORK_ROCKET',
  'DIAMOND_PICKAXE', 'DIAMOND_SWORD', 'GOLDEN_APPLE', 'TNT', 'BONE', 'SLIME_BALL',
  'BARREL', 'HOPPER', 'MINECART', 'BELL', 'ENDER_EYE', 'WRITTEN_BOOK',
];

/** Tier icons follow the tier names, which are Minecraft materials already. */
const TIER_MATERIAL: Record<(typeof TIER_ORDER)[number], string> = {
  free: 'PAPER',
  wood: 'OAK_PLANKS',
  iron: 'IRON_INGOT',
  gold: 'GOLD_INGOT',
  diamond: 'DIAMOND',
  netherite: 'NETHERITE_INGOT',
};

/** What each tier adds over the one before it, so the rail reads as a ladder. */
const TIER_POINTS: Record<(typeof TIER_ORDER)[number], string[]> = {
  free: ['Your 5 busiest signs', 'Price vs market on a few items', '7 days of history'],
  wood: ['Full analytics on every item', 'Undercut alerts', 'Demand by hour', 'Restock detection'],
  iron: ['Everything in Wood', 'Up to 3 locations', 'Portfolio across shops', '90 days of history'],
  gold: ['Everything in Iron', 'Opportunity finder', 'Pricing recommendations', 'Discord alerts'],
  diamond: ['Everything in Gold', 'Up to 10 locations', 'Data export', 'A year of history'],
  netherite: ['Everything in Diamond', 'No shop or location limit', 'Priority refresh'],
};

const BEFORE = [
  'Walk the market and write competitor prices on paper.',
  'Find out you were undercut when the chest stops emptying.',
  'Guess whether a slow item is priced wrong or just unwanted.',
  'Restock whenever you happen to log in.',
  'No idea which items are worth stocking next.',
];

const AFTER = [
  'Every competitor price for every item you sell, on one screen.',
  'Told the moment someone prices under you.',
  'A suggested price with the reasoning shown, per item.',
  'See exactly which chests are empty right now.',
  'A ranked list of items the market wants and few shops supply.',
];

const STEPS = [
  {
    title: 'Create an account',
    detail: 'Email and a password. Pick whether you are a shop owner, a buyer or a courier.',
  },
  {
    title: 'Type your in-game name',
    detail:
      'Your chestshops appear immediately. Nothing to authorise, nothing to install.',
  },
  {
    title: 'Add a webhook later, if you want',
    detail:
      'Optional, and set up on the DemocracyCraft site in a couple of clicks. It adds your exact sales history on top of everything above.',
  },
];

const FAQ = [
  {
    q: 'Does it work before I connect anything?',
    a: 'Yes - that is the whole design. Type your name and your signs, prices and stock are there. A webhook later only adds your exact sales history on top.',
  },
  {
    q: 'How fresh is the data?',
    a: 'We sweep the whole market every six hours and the busiest items every fifteen minutes, and you can force a re-read of any item. The server publishes a periodic snapshot rather than a live feed, so we show how old each reading is instead of pretending it is instant.',
  },
  {
    q: 'I only have a handful of shops. Is this for me?',
    a: 'Yes, and you can find out for nothing. Start on the Free plan, then take the free first month of Wood - no payment, no card, and it unlocks the full analytics on every sign you run. What a plan costs is set by how big your shop is, so a small shop pays little; what it saves you is set by how badly a price is wrong, and one item priced 30% under the market costs more than a month of Wood.',
  },
  {
    q: 'What happens if I outgrow my plan?',
    a: 'Nothing breaks. Signs beyond your plan are hidden from the dashboard with a plain count of how many, and you keep your biggest location in full. Add-ons cover a small overrun without a whole tier jump.',
  },
  {
    q: 'Is this allowed on DemocracyCraft?',
    a: 'It reads the published Economy API the way the documentation describes, with no scraping and no in-game automation. Payments are ordinary in-game transfers.',
  },
];

/**
 * The three people this is for, each led by what they get rather than by what
 * the product does. The buyer block is first on purpose: it is free, it is the
 * widest door in, and a buyer who becomes a seller is the whole funnel.
 */
const AUDIENCES = [
  {
    key: 'buyers',
    emoji: '\u{1F50E}',
    title: 'For Buyers',
    tag: 'free, forever',
    cta: 'Browse the Market',
    href: '/login?role=buyer',
    lede:
      'Search every chestshop at once. See low, high, and average prices per item, spot the best deal, and skip the fake shops.',
    points: [
      'Real names for enchanted & hidden items \u2014 finally searchable',
      'Item images, so you know what you are buying',
      'Low / high / avg / recommended price on everything',
      'Empty-shop flags \u2014 never walk to a dead shop again',
    ],
  },
  {
    key: 'owners',
    emoji: '\u{1F4C8}',
    title: 'For Store Owners',
    tag: 'sell more',
    cta: 'List Your Shop',
    href: '/login?role=store_owner',
    lede:
      'Stop guessing your prices. Chestr shows you exactly where you stand against the market and when buyers are actually shopping.',
    points: [
      'Live price benchmarking vs every competitor',
      'Undercut & restock alerts',
      'Demand heatmaps \u2014 know your peak hours',
      'The Opportunities tab: high demand, low supply, your next big seller',
    ],
  },
  {
    key: 'runners',
    emoji: '\u{1F69A}',
    title: 'For Runners',
    tag: 'get paid to deliver',
    cta: 'Join the waitlist',
    href: '/login?role=delivery',
    lede: 'Pick up jobs, run the orders, earn. Secure escrow means you always get paid.',
    points: [
      'Guaranteed payment via escrow',
      'Auto-generated pickup routes on the town metro/bus system',
      'Bonus jobs: verify items, fill in missing names',
    ],
  },
];

const ALSO = [
  'Check your shop from your phone \u2014 no need to be in game',
  'Sales history and trends, daily, weekly or monthly',
  'Multi-shop portfolio across every location you run',
  'Discord alerts',
];
